import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type ProfileModuleDto,
  ProfileModuleType,
  type ProfileTemplateFieldMode,
} from '@up2stay/up2promo-api-client';
import {
  graphemeLength,
  sliceGraphemes,
  toExternalHref,
} from '@up2stay/up2promo-commons';
import { Button } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { ConfirmDialog } from '@up2stay/up2promo-ui';
import { InlineEditCard } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { ExternalLink, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';

import type { ModuleEntryGovernanceProps } from '@/core/components/profile/module-entry-governance';
import {
  ModuleEntryListRowBody,
  ModuleEntryLockedHint,
  ModuleEntryOverrideHint,
  ModuleEntryPolicySlot,
} from '@/core/components/profile/module-entry-governance-ui';
import { TextareaWithCharCount } from '@/core/components/ui/field-with-char-count';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import {
  isEntryPolicyLocked,
  resolveEntryPolicy,
} from '@/core/lib/profile-module-entry-policies';
import type { LinkItem, LinksModuleConfig } from '@/core/types/profile-modules';

const LINK_DESCRIPTION_MAX_LENGTH = 200;

function reorderItems<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...items];
  const [removed] = result.splice(fromIndex, 1);
  if (removed === undefined) return result;
  result.splice(toIndex, 0, removed);
  return result;
}

interface EditFormState {
  title: string;
  url: string;
  description: string;
  errors: { title?: string; url?: string };
}

function getEmptyFormState(): EditFormState {
  return { title: '', url: '', description: '', errors: {} };
}

function getFormStateFromItem(item: LinkItem): EditFormState {
  return {
    title: item.title ?? '',
    url: item.url ?? '',
    description: sliceGraphemes(
      item.description ?? '',
      LINK_DESCRIPTION_MAX_LENGTH,
    ),
    errors: {},
  };
}

interface LinkEditFormProps {
  state: EditFormState;
  onChange: (next: EditFormState) => void;
}

function LinkEditForm({ state, onChange }: LinkEditFormProps) {
  // Ties each visible <Label> to its field for screen readers (DEV-540).
  const fieldId = useId();

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={`${fieldId}-title`}>Titel *</Label>
        <Input
          id={`${fieldId}-title`}
          placeholder="z.B. Mein Blog"
          value={state.title}
          onChange={(e) =>
            onChange({
              ...state,
              title: e.target.value,
              errors: { ...state.errors, title: undefined },
            })
          }
          className={cn(state.errors.title && 'border-destructive')}
          aria-invalid={!!state.errors.title}
        />
        {state.errors.title ? (
          <p className="text-sm text-destructive">{state.errors.title}</p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${fieldId}-url`}>URL *</Label>
        <Input
          id={`${fieldId}-url`}
          type="text"
          inputMode="url"
          autoComplete="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="https://…"
          value={state.url}
          onChange={(e) =>
            onChange({
              ...state,
              url: e.target.value,
              errors: { ...state.errors, url: undefined },
            })
          }
          className={cn(state.errors.url && 'border-destructive')}
          aria-invalid={!!state.errors.url}
        />
        {state.errors.url ? (
          <p className="text-sm text-destructive">{state.errors.url}</p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${fieldId}-description`}>
          Beschreibung (optional)
        </Label>
        <TextareaWithCharCount
          id={`${fieldId}-description`}
          placeholder="Kurze Beschreibung"
          value={state.description}
          onChange={(e) => {
            const v = sliceGraphemes(
              e.target.value,
              LINK_DESCRIPTION_MAX_LENGTH,
            );
            onChange({ ...state, description: v });
          }}
          rows={2}
          maxLength={LINK_DESCRIPTION_MAX_LENGTH}
          valueLength={graphemeLength(state.description)}
        />
      </div>
    </>
  );
}

interface SortableLinkItemProps extends Omit<
  ModuleEntryGovernanceProps,
  'onEntryPolicyChange' | 'showEntryPolicies'
> {
  item: LinkItem;
  index: number;
  onEdit: () => void;
  onRemove: () => void;
  fieldLockedHintMessage?: string;
  entryLocked?: boolean;
  showEntryPolicy?: boolean;
  entryPolicy?: ProfileTemplateFieldMode;
  onEntryPolicyChange?: (mode: ProfileTemplateFieldMode) => void;
  currentModule?: ProfileModuleDto;
  entryId?: string;
  onResetEntry?: () => void;
}

function SortableLinkEditSlot({
  index,
  children,
}: {
  index: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, transform, transition, isDragging } = useSortable({
    id: `link-${index}`,
    disabled: true,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children}
    </div>
  );
}

function SortableLinkItem({
  item,
  index,
  onEdit,
  onRemove,
  structureReadOnly = false,
  entryLocked = false,
  showEntryPolicy = false,
  entryPolicy,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  showEntryOverrideHints = false,
  currentModule,
  templateModule,
  entryId,
  overrideHintMessage,
  templateOverrideLabels,
  onResetEntry,
  workspaceId,
  templateId,
  publishStatus,

  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
  contentReadOnly = false,
}: SortableLinkItemProps) {
  const isMobile = useIsMobile();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `link-${index}`, disabled: structureReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : 'none',
  };

  const displayLabel = item.title?.trim() || item.url || 'Link';

  const policySlot = (
    <ModuleEntryPolicySlot
      showEntryPolicy={showEntryPolicy}
      entryPolicy={entryPolicy}
      onEntryPolicyChange={onEntryPolicyChange}
      policyLabels={policyLabels}
      memberValueColumn={
        entryId
          ? {
              kind: 'moduleEntry',
              entryId,
              moduleId: templateModule?.id ?? currentModule?.id,
              label: displayLabel,
            }
          : undefined
      }
      memberDialogTitle={displayLabel}
      memberDefaultValue={item.url ?? ''}
      memberInputPlaceholder="https://"
      workspaceId={workspaceId}
      templateId={templateId}
      publishStatus={publishStatus}
      memberRoster={memberRoster}
      memberRosterLoading={memberRosterLoading}
      hasAssignedTeams={hasAssignedTeams}
      memberValuesDict={memberValuesDict}
      canManage={!contentReadOnly}
    />
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border',
        isDragging &&
          'opacity-80 shadow-lg z-10 bg-background ring-2 ring-primary',
        isMobile && 'touch-pan-y cursor-grab active:cursor-grabbing',
      )}
      {...(isMobile ? { ...listeners, ...attributes } : {})}
    >
      <ModuleEntryListRowBody
        isMobile={isMobile}
        structureReadOnly={structureReadOnly}
        dragHandle={
          <div
            data-dnd-grip
            {...listeners}
            {...attributes}
            className={cn(
              'touch-none shrink-0 rounded p-0.5 cursor-grab active:cursor-grabbing',
            )}
            aria-label="Reihenfolge ändern"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        }
        main={
          <button
            type="button"
            onClick={onEdit}
            disabled={entryLocked && !showEntryPolicy}
            className="flex w-full min-w-0 cursor-pointer items-center gap-3 text-left rounded-md p-2 -m-2 disabled:cursor-default disabled:opacity-70"
          >
            <ExternalLink className="h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{displayLabel}</div>
              {item.url ? (
                <div className="text-sm text-muted-foreground truncate">
                  {item.url}
                </div>
              ) : null}
              <ModuleEntryLockedHint
                entryLocked={entryLocked}
                fieldLockedHintMessage={fieldLockedHintMessage}
                className="mt-1"
              />
            </div>
          </button>
        }
        policy={policySlot}
        trailing={
          !structureReadOnly ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 opacity-70 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label="Link entfernen"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null
        }
      />
      {currentModule &&
      templateModule &&
      entryId &&
      onResetEntry &&
      templateOverrideLabels ? (
        <ModuleEntryOverrideHint
          currentModule={currentModule}
          templateModule={templateModule}
          entryId={entryId}
          showEntryOverrideHints={showEntryOverrideHints}
          overrideHintMessage={overrideHintMessage}
          templateOverrideLabels={templateOverrideLabels}
          onReset={onResetEntry}
          className="mt-1.5 px-3 pb-2"
        />
      ) : null}
    </div>
  );
}

interface LinksModuleConfigProps extends ModuleEntryGovernanceProps {
  config: LinksModuleConfig;
  onChange: (config: LinksModuleConfig) => void;
  fieldLockedHintMessage?: string;
}

/** -1 = adding new, 0+ = editing that index, null = idle */
type EditingIndex = number | null;

export function LinksModuleConfigComponent({
  config,
  onChange,
  structureReadOnly = false,
  contentReadOnly = false,
  templateModule,
  showEntryPolicies = false,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  showEntryOverrideHints = false,
  templateOverrideLabels,
  overrideHintMessage,
  workspaceId,
  templateId,
  publishStatus,

  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
}: LinksModuleConfigProps) {
  const isMobile = useIsMobile();
  const [localConfig, setLocalConfig] = useState<LinksModuleConfig>(config);
  const [prevConfig, setPrevConfig] = useState(config);
  if (config !== prevConfig) {
    setPrevConfig(config);
    setLocalConfig(config);
  }
  const [editingIndex, setEditingIndex] = useState<EditingIndex>(null);
  const [formState, setFormState] = useState<EditFormState>(getEmptyFormState);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const handleChange = (updates: Partial<LinksModuleConfig>) => {
    if (contentReadOnly) return;
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const items = localConfig.items || [];

  const currentModuleDto = useMemo(
    (): ProfileModuleDto => ({
      type: ProfileModuleType.LINKS,
      order: 0,
      enabled: true,
      config: localConfig as unknown as Record<string, unknown>,
    }),
    [localConfig],
  );

  const resetEntryToTemplate = (entryId: string) => {
    const templateItems = (templateModule?.config?.items as LinkItem[]) ?? [];
    const templateEntry = templateItems.find((entry) => entry.id === entryId);
    if (!templateEntry) return;
    const { policy: _policy, ...templateData } = templateEntry;
    handleChange({
      items: items.map((item) =>
        item.id === entryId ? { ...templateData, id: entryId } : item,
      ),
    });
  };

  const startAdd = () => {
    if (contentReadOnly) return;
    setEditingIndex(-1);
    setFormState(getEmptyFormState());
  };

  const startEdit = (index: number) => {
    if (contentReadOnly) return;
    const item = items[index];
    if (!item) return;
    setEditingIndex(index);
    setFormState(getFormStateFromItem(item));
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setFormState(getEmptyFormState());
  };

  const saveEdit = () => {
    if (editingIndex == null) return;
    const title = formState.title.trim();
    const url = toExternalHref(formState.url);
    const errors: { title?: string; url?: string } = {};
    if (!title) errors.title = 'Titel ist erforderlich.';
    if (!url) errors.url = 'URL ist erforderlich.';
    if (Object.keys(errors).length > 0) {
      setFormState({ ...formState, errors });
      return;
    }
    const desc = sliceGraphemes(
      formState.description,
      LINK_DESCRIPTION_MAX_LENGTH,
    );
    const existing = editingIndex >= 0 ? items[editingIndex] : undefined;
    const entryId = existing?.id ?? crypto.randomUUID();
    const newItem: LinkItem = {
      id: entryId,
      title,
      url,
      description: desc.trim() || undefined,
      openInNewTab: existing?.openInNewTab ?? true,
      order: editingIndex === -1 ? items.length : editingIndex,
    };
    if (editingIndex === -1) {
      handleChange({ items: [...items, newItem] });
    } else {
      const newItems = [...items];
      newItems[editingIndex] = { ...existing, ...newItem };
      handleChange({ items: newItems });
    }
    setEditingIndex(null);
    setFormState(getEmptyFormState());
  };

  const requestRemove = (index: number) => {
    setPendingRemoveIndex(index);
  };

  const confirmRemove = () => {
    if (pendingRemoveIndex == null) return;
    const index = pendingRemoveIndex;
    const newItems = items.filter((_, i) => i !== index);
    handleChange({ items: newItems });
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (editingIndex != null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
    setPendingRemoveIndex(null);
  };

  const lastOverIdRef = useRef<string | null>(null);

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (over == null) {
      lastOverIdRef.current = null;
      return;
    }
    const isNewSlot = lastOverIdRef.current !== over.id;
    const isBackToOwnSlot =
      over.id === active.id && lastOverIdRef.current === null;
    if (isNewSlot && !isBackToOwnSlot) {
      void triggerDragOverHaptic();
    }
    lastOverIdRef.current = over.id != null ? String(over.id) : null;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (structureReadOnly || contentReadOnly) return;
    const { active, over } = event;
    if (over == null || active.id === over.id) return;
    const fromIndex = items.findIndex((_, i) => `link-${i}` === active.id);
    const toIndex = items.findIndex((_, i) => `link-${i}` === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    const reordered = reorderItems(items, fromIndex, toIndex).map(
      (item, i) => ({ ...item, order: i }),
    );
    handleChange({ items: reordered });
    if (editingIndex !== null && editingIndex >= 0) {
      if (editingIndex === fromIndex) {
        setEditingIndex(toIndex);
      } else if (fromIndex < editingIndex && toIndex >= editingIndex) {
        setEditingIndex(editingIndex - 1);
      } else if (fromIndex > editingIndex && toIndex <= editingIndex) {
        setEditingIndex(editingIndex + 1);
      }
    }
  };

  const sensors = useDndProfileSensors();

  const isAdding = editingIndex === -1;
  const isEditingExisting = editingIndex != null && editingIndex >= 0;

  const pendingRemoveItem =
    pendingRemoveIndex != null ? items[pendingRemoveIndex] : undefined;
  const pendingRemoveLabel =
    pendingRemoveItem?.title?.trim() || pendingRemoveItem?.url || '';

  const inlineEditCard =
    editingIndex !== null ? (
      <InlineEditCard
        variant="bare"
        asSheet={isMobile}
        title={isAdding ? 'Link hinzufügen' : 'Link bearbeiten'}
        icon={isMobile ? undefined : <ExternalLink className="h-5 w-5" />}
        onSave={saveEdit}
        onCancel={cancelEdit}
      >
        <LinkEditForm state={formState} onChange={setFormState} />
      </InlineEditCard>
    ) : null;

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label>Links</Label>
        {items.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={() => {
              lastOverIdRef.current = null;
              lockBodyScrollDuringDrag();
              void triggerDragStartHaptic();
            }}
            onDragOver={handleDragOver}
            onDragEnd={(e) => {
              handleDragEnd(e);
              unlockBodyScrollAfterDrag();
              void triggerDragEndHaptic();
            }}
          >
            <SortableContext
              items={items.map((_, i) => `link-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {items.map((item, index) => {
                  const entryId = item.id ?? String(index);
                  return isEditingExisting &&
                    editingIndex === index &&
                    !isMobile ? (
                    <SortableLinkEditSlot key={index} index={index}>
                      {inlineEditCard}
                    </SortableLinkEditSlot>
                  ) : (
                    <SortableLinkItem
                      key={index}
                      item={item}
                      index={index}
                      onEdit={() => startEdit(index)}
                      onRemove={() => requestRemove(index)}
                      structureReadOnly={structureReadOnly}
                      entryLocked={
                        contentReadOnly ||
                        (templateModule
                          ? isEntryPolicyLocked(templateModule, entryId)
                          : false)
                      }
                      showEntryPolicy={showEntryPolicies}
                      entryPolicy={
                        templateModule
                          ? resolveEntryPolicy(templateModule, entryId)
                          : undefined
                      }
                      onEntryPolicyChange={
                        onEntryPolicyChange
                          ? (mode) => onEntryPolicyChange(entryId, mode)
                          : undefined
                      }
                      policyLabels={policyLabels}
                      fieldLockedHintMessage={fieldLockedHintMessage}
                      showEntryOverrideHints={showEntryOverrideHints}
                      currentModule={currentModuleDto}
                      templateModule={templateModule}
                      entryId={entryId}
                      overrideHintMessage={overrideHintMessage}
                      templateOverrideLabels={templateOverrideLabels}
                      onResetEntry={() => resetEntryToTemplate(entryId)}
                      workspaceId={workspaceId}
                      templateId={templateId}
                      publishStatus={publishStatus}
                      memberRoster={memberRoster}
                      memberRosterLoading={memberRosterLoading}
                      hasAssignedTeams={hasAssignedTeams}
                      memberValuesDict={memberValuesDict}
                      contentReadOnly={contentReadOnly}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        ) : null}

        {isAdding && !isMobile ? (
          inlineEditCard
        ) : !structureReadOnly && !contentReadOnly ? (
          <button
            type="button"
            onClick={startAdd}
            disabled={isEditingExisting}
            className={cn(
              'group flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 px-4 py-5',
              'text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary',
              'disabled:opacity-50 disabled:pointer-events-none',
            )}
            aria-label="Link hinzufügen"
          >
            <Plus className="h-5 w-5" />
            <span className="font-medium">Link hinzufügen</span>
          </button>
        ) : null}
        {isMobile ? inlineEditCard : null}
      </div>

      <ConfirmDialog
        open={pendingRemoveIndex !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoveIndex(null);
        }}
        title="Link entfernen?"
        description={
          pendingRemoveLabel ? (
            <>
              Möchtest du{' '}
              <span className="font-medium">{pendingRemoveLabel}</span> wirklich
              entfernen? Diese Aktion kann nicht rückgängig gemacht werden.
            </>
          ) : (
            'Möchtest du diesen Link wirklich entfernen? Diese Aktion kann nicht rückgängig gemacht werden.'
          )
        }
        confirmLabel="Entfernen"
        cancelLabel="Abbrechen"
        destructive
        onConfirm={confirmRemove}
      />
    </div>
  );
}
